import { useTheme } from "styled-components";
import { useLanguageContext } from "../../../../context/LanguageContext";
import { StyledInput } from "../../../../styles/InputStyles";
import InputWrapper from "../../../../components/input/wrapper/InputWrapper";
import {
  StyledFlexColumn,
  StyledFlexItem,
  StyledFlexRow,
} from "../../../../styles/LayoutStyles";
import SchedulingForm from "../../../../components/schedulingForm/SchedulingForm";
import OverlayLoader from "../../../../components/loader/OverlayLoader/OverlayLoader";
import FormContainer from "../../../../components/formContainer/FormContainer";
import EVAApiHelper from "../../../../helpers/EVAApiHelper";
import MultiSelect from "../../../../components/select/main/multi/MultiSelect";
import LocationFormSelect from "../../../../components/select/custom/location/form/LocationFormSelect";
import ImageSelect from "../../../../components/select/custom/image/ImageSelect";

function DishPageView({
  dish,
  dishData,
  allergens,
  images,
  onLocationIdChange,
  onImageChange,
  onNameChange,
  onPriceChange,
  onCapacityChange,
  onCaloriesChange,
  onHealthScoreChange,
  onCategoryChange,
  onDescriptionChange,
  onAllergensChange,
  onScheduleChange,
  onCancel,
  onSubmit,
  submitError,
}) {
  const theme = useTheme();
  const { translate } = useLanguageContext();

  return (
    <StyledFlexColumn style={{ gap: theme.spacing.large }}>
      <OverlayLoader groups="canteen_dish_page_fetch">
        <FormContainer
          nullLocation={false}
          locationValue={dishData.location_id}
          onLocationChange={onLocationIdChange}
          locationDisabled={dish !== undefined}
          onCancel={onCancel}
          onSubmit={onSubmit}
          submitError={submitError}
          submitLoadingGroup={"canteen_dish_page_save"}
        >
          <InputWrapper
            label={translate("eva.main.entities.location")}
            error={EVAApiHelper.getResponseError("location_id", submitError)}
            margin={`0px 0px ${theme.spacing.normal}`}
          >
            <LocationFormSelect
              value={dishData.location_id}
              onChange={onLocationIdChange}
              disabled={dish !== undefined}
              useNullOption={false}
            />
          </InputWrapper>

          <StyledFlexRow
            style={{ margin: `0px 0px ${theme.spacing.normal} 0px` }}
          >
            {/* Image input */}
            <InputWrapper
              error={EVAApiHelper.getResponseError("image_id", submitError)}
            >
              <ImageSelect
                images={images}
                value={dishData.image_id}
                onChange={onImageChange}
              />
            </InputWrapper>

            <StyledFlexItem>
              <StyledFlexRow
                style={{ margin: `0px 0px ${theme.spacing.normal} 0px` }}
              >
                <StyledFlexItem>
                  {/* Name input */}
                  <InputWrapper
                    label={translate("eva.main.validation.attributes.name")}
                    required
                    error={EVAApiHelper.getResponseError("name", submitError)}
                  >
                    <StyledInput
                      type="text"
                      placeholder={translate(
                        "eva.main.validation.attributes.name"
                      )}
                      value={dishData.name}
                      onChange={onNameChange}
                    />
                  </InputWrapper>
                </StyledFlexItem>

                {/* Price input */}
                <InputWrapper
                  label={translate("eva.main.validation.attributes.price")}
                  required
                  error={EVAApiHelper.getResponseError("price", submitError)}
                >
                  <StyledInput
                    type="number"
                    value={dishData.price}
                    onChange={onPriceChange}
                  />
                </InputWrapper>

                {/* Capacity input */}
                <InputWrapper
                  label={translate("eva.main.validation.attributes.capacity")}
                  required
                  error={EVAApiHelper.getResponseError("capacity", submitError)}
                >
                  <StyledInput
                    type="number"
                    value={dishData.capacity}
                    onChange={onCapacityChange}
                  />
                </InputWrapper>
              </StyledFlexRow>

              {/* Description input */}
              <InputWrapper
                label={translate("eva.main.validation.attributes.description")}
                error={EVAApiHelper.getResponseError(
                  "description",
                  submitError
                )}
              >
                <StyledInput
                  as="textarea"
                  rows={4}
                  placeholder={translate(
                    "eva.main.validation.attributes.description"
                  )}
                  value={dishData.description}
                  onChange={onDescriptionChange}
                />
              </InputWrapper>
            </StyledFlexItem>
          </StyledFlexRow>

          {/* Category input */}
          <InputWrapper
            style={{ marginBottom: theme.spacing.normal }}
            label={translate("eva.main.validation.attributes.category")}
            error={EVAApiHelper.getResponseError("category", submitError)}
          >
            <StyledInput
              type="text"
              value={dishData.category}
              onChange={onCategoryChange}
              placeholder={translate("eva.main.validation.attributes.category")}
            />
          </InputWrapper>

          {/* Allergen select */}
          <InputWrapper
            label={translate("eva.main.entities.allergens")}
            error={EVAApiHelper.getResponseErrorArray(
              "allergens",
              submitError,
              (index) =>
                translate(`eva.main.allergens.${allergens[index].name}`)
            )}
          >
            <MultiSelect
              options={allergens.map((allergen) => {
                return {
                  value: allergen.id,
                  label: translate(`eva.main.allergens.${allergen.name}`),
                };
              })}
              value={dishData.allergens}
              onChange={onAllergensChange}
            />
          </InputWrapper>

          <StyledFlexRow style={{ marginBlock: theme.spacing.normal }}>
            <StyledFlexItem>
              <InputWrapper
                label={translate("eva.main.validation.attributes.calories")}
                error={EVAApiHelper.getResponseError("calories", submitError)}
              >
                <StyledInput
                  type="number"
                  value={dishData.calories}
                  onChange={onCaloriesChange}
                  placeholder={translate(
                    "eva.main.validation.attributes.calories"
                  )}
                />
              </InputWrapper>
            </StyledFlexItem>

            <StyledFlexItem>
              <InputWrapper
                label={translate("eva.main.validation.attributes.health_score")}
                error={EVAApiHelper.getResponseError(
                  "health_score",
                  submitError
                )}
              >
                <StyledInput
                  type="number"
                  value={dishData.health_score}
                  onChange={onHealthScoreChange}
                  placeholder={translate(
                    "eva.main.validation.attributes.health_score"
                  )}
                />
              </InputWrapper>
            </StyledFlexItem>
          </StyledFlexRow>
        </FormContainer>
      </OverlayLoader>

      <OverlayLoader groups="canteen_dish_page_fetch">
        <SchedulingForm
          schedule={dishData.schedule}
          onScheduleChange={onScheduleChange}
          submitError={submitError}
        />
      </OverlayLoader>
    </StyledFlexColumn>
  );
}

export default DishPageView;
